import React, { FC, useMemo } from 'react'

import { RewardsLevel, RewardsQuery } from '../../../../../../../graphql/search'
import { useCustomerVipDiscount } from '../../../../../../../lib/m2/hooks/useCustomerVipDiscount'
import { usePractitionerSettings } from '../../../../../../../lib/practitioner-settings/hooks/usePractitionerSettings'
import {
  nextRewardsLevel,
  rewardsLevelsInfo,
  tierFromRewardsLevel,
} from '../../../../../../../lib/rewards/levels'
import { dropZeroCents, formatAsCurrencyWithCommas } from '../../../../../../../utils/priceTools'
import { RewardsIconWrapper } from '../../../RewardsIcon'
import { RewardsInformationTooltip } from '../../../RewardsInformationTooltip'
import * as Styled from './styled'

import CurrentTier from '../../../../../../../img/rewards/current-tier.inline.svg'

export interface TierDetailsCardProps {
  currentSnapshot?: NonNullable<
    NonNullable<RewardsQuery['customer']>['rewardsSnapshots']
  >['nodes'][number]
}

export const TierDetailsCard: FC<TierDetailsCardProps> = ({ currentSnapshot }) => {
  const currentLevel = currentSnapshot?.level ?? RewardsLevel.Level_1
  const currentTier = tierFromRewardsLevel(currentLevel)
  const currentTierInfo = useMemo(
    () => rewardsLevelsInfo.find((info) => info.level === currentLevel),
    [currentLevel],
  )
  const currentSales = currentSnapshot?.sales.total.amount
    ? parseFloat(currentSnapshot.sales.total.amount)
    : 0

  const nextLevel = nextRewardsLevel(currentLevel)
  const nextTier = tierFromRewardsLevel(nextLevel)
  const nextTierInfo = useMemo(
    () => rewardsLevelsInfo.find((info) => info.level === nextLevel),
    [nextLevel],
  )
  const remainingSales = nextTierInfo
    ? Math.max(0, nextTierInfo.salesThreshold.minimum - currentSales)
    : 0

  const [settings] = usePractitionerSettings()
  const optedOutOfCommissions = settings?.practitioner?.commissionsOptOutFormReceived ?? false
  const isEligibleForRebates = !settings?.practitioner?.ineligibleForRebate
  const isEligibleForCommissions = !settings?.practitioner?.ineligibleForCommissions

  const { isEligibleForVipDiscount } = useCustomerVipDiscount()

  return (
    <Styled.Card>
      <Styled.CurrentTierHeaderSection>
        <RewardsIconWrapper>
          <CurrentTier />
        </RewardsIconWrapper>
        <Styled.CurrentTierSection>
          <Styled.SalesOverview>Currently in Tier {currentTier}</Styled.SalesOverview>
          {nextTier && remainingSales > 0 && (
            <Styled.UnlockTierText>
              <strong>
                {dropZeroCents(formatAsCurrencyWithCommas({ amount: remainingSales }))}
              </strong>{' '}
              more to unlock
              <strong> Tier {nextTier}</strong>
            </Styled.UnlockTierText>
          )}
        </Styled.CurrentTierSection>
      </Styled.CurrentTierHeaderSection>
      {currentTierInfo && (
        <Styled.TierInfoSection>
          {isEligibleForCommissions && (
            <Styled.TierInfoItem>
              {optedOutOfCommissions ? (
                <>
                  <Styled.TierInfoValue>0%</Styled.TierInfoValue>
                  <Styled.TierInfoLabel>
                    {`You're missing out on commissions up to ${currentTierInfo.benefits.commissionsPercentage.toFixed(1).replace(/\.0$/, '')}%. `}
                    <Styled.Link to="/account/settings/rewards-program">Enroll here</Styled.Link>
                  </Styled.TierInfoLabel>
                  <RewardsInformationTooltip placement="left">
                    Applicable to <strong>this month orders</strong>.
                  </RewardsInformationTooltip>
                </>
              ) : (
                <>
                  <Styled.TierInfoValue>
                    {currentTierInfo.benefits.commissionsPercentage.toFixed(1)}%
                  </Styled.TierInfoValue>
                  <Styled.TierInfoLabel>Commissions on patient-direct sales</Styled.TierInfoLabel>
                  <RewardsInformationTooltip placement="left">
                    Applicable to <strong>this month orders</strong>.
                  </RewardsInformationTooltip>
                </>
              )}
            </Styled.TierInfoItem>
          )}
          {isEligibleForRebates && (
            <Styled.TierInfoItem>
              <Styled.TierInfoValue>
                {currentTierInfo.benefits.rebatePercentage.toFixed(1)}%
              </Styled.TierInfoValue>
              <Styled.TierInfoLabel>Monthly rebate</Styled.TierInfoLabel>
              <RewardsInformationTooltip placement="left">
                Applicable to <strong>this month orders</strong>.
              </RewardsInformationTooltip>
            </Styled.TierInfoItem>
          )}
          {isEligibleForVipDiscount && (
            <>
              {(isEligibleForCommissions || isEligibleForRebates) && <Styled.TierLine />}
              <Styled.TierInfoItem>
                <Styled.TierInfoValue>
                  {currentTierInfo.benefits.vipDiscountPercentage.toFixed(1)}%
                </Styled.TierInfoValue>
                <Styled.TierInfoLabel>
                  VIP Discount on in-practice orders for next month
                </Styled.TierInfoLabel>
                <RewardsInformationTooltip placement="left">
                  Applicable to <strong>next month orders</strong>.
                </RewardsInformationTooltip>
              </Styled.TierInfoItem>
            </>
          )}
        </Styled.TierInfoSection>
      )}
    </Styled.Card>
  )
}
